import React, { useEffect, useState } from 'react';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Loader2, ExternalLink } from 'lucide-react';

// A zone read by the www1-fiche reader (spec 127): every norme in its state, each with its fiche, page and date.
// Each norme reads with the label the API serves beside it (norme_labels, the norme contract; fix 144, P4).

const number = (v) => (v === null || v === undefined ? '—' : String(v).replace('.', ','));

const tierLabel = (t) => {
  if (t.above_m === null) return `Jusqu’à ${number(t.up_to_m)} m`;
  if (t.up_to_m === null) return `Plus de ${number(t.above_m)} m`;
  return `Plus de ${number(t.above_m)} m jusqu’à ${number(t.up_to_m)} m`;
};

const StateValue = ({ name, state }) => {
  if (state.state === 'unverifiable') {
    return <span className="text-amber-700" data-testid={`norme-${name}-value`} title={state.reason}>non vérifiable</span>;
  }
  if (state.state === 'not_prescribed') {
    return <span className="text-muted-foreground" data-testid={`norme-${name}-value`}>non prescrit</span>;
  }
  if (name === 'usages_categories') {
    return (
      <span className="flex flex-wrap gap-1" data-testid={`norme-${name}-value`}>
        {state.value.map((c, i) => <Badge key={c} variant="outline" data-testid={`usage-category-${i + 1}`}>{`Catégorie ${i + 1} : ${c}`}</Badge>)}
      </span>
    );
  }
  if (name === 'marges_par_hauteur') {
    return (
      <table className="text-sm" data-testid={`norme-${name}-value`}>
        <thead><tr><th className="pr-4 text-left">Hauteur</th><th className="pr-4">Latérale min. (m)</th><th>Arrière min. (m)</th></tr></thead>
        <tbody>
          {state.value.map((t, i) => (
            <tr key={tierLabel(t)} data-testid={`marge-tier-${i + 1}`}>
              <td className="pr-4">{tierLabel(t)}</td><td className="pr-4 text-center">{number(t.laterale_min_m)}</td><td className="text-center">{number(t.arriere_min_m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <span data-testid={`norme-${name}-value`}>{state.as_written}</span>;
};

export const ReadZoneSheet = ({ territoireId, zoneCode }) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/regulations/${encodeURIComponent(territoireId)}/zones/${encodeURIComponent(zoneCode)}`)
      .then((res) => { if (active) setDoc(res.data); })
      .catch((e) => { if (active) setError(e.response?.data?.detail || 'Erreur de chargement de la zone'); });
    return () => { active = false; };
  }, [territoireId, zoneCode]);

  if (error) {
    return (
      <Alert variant="destructive" data-testid="read-zone-error">
        <AlertTitle>Zone introuvable</AlertTitle>
        <AlertDescription>{String(error)}</AlertDescription>
      </Alert>
    );
  }
  if (!doc) {
    return <div className="flex items-center gap-2 text-muted-foreground p-8"><Loader2 className="w-4 h-4 animate-spin" /> Chargement de la fiche…</div>;
  }

  return (
    <Card data-testid="read-zone-sheet">
      <CardHeader>
        <CardTitle>Zone <span className="font-mono" data-testid="read-zone-code">{doc.zone_code}</span> — {doc.jurisdiction}</CardTitle>
        <CardDescription data-testid="read-zone-citation">{doc.reglement_citation}</CardDescription>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b"><th className="py-2">Norme</th><th>Valeur</th><th>Source</th></tr>
          </thead>
          <tbody>
            {Object.entries(doc.normes).map(([name, state]) => (
              <tr key={name} className="border-b align-top" data-testid={`norme-${name}`}>
                <td className="py-2 pr-4" data-testid={`norme-${name}-label`}>{doc.norme_labels[name]}</td>
                <td className="py-2 pr-4"><StateValue name={name} state={state} /></td>
                <td className="py-2 text-xs text-muted-foreground" data-testid={`norme-${name}-provenance`}>
                  <a href={state.provenance.document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                    fiche, page {state.provenance.page} <ExternalLink className="w-3 h-3" />
                  </a>
                  {` · extraite le ${state.provenance.extraction_date}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {doc.notes?.length > 0 && (
          <ul className="mt-4 text-sm list-disc pl-5" data-testid="read-zone-notes">
            {doc.notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ReadZoneSheet;
